import { AppBar, Toolbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { color } from '../../app/theme';
import { MAStatusType, TabType } from '../../app/types';
import defaultImageUrl from '../../assets/images/svg/img_avatar_org.svg';
import logo from '../../assets/next/logo/ia-logo.png';
import {
  saveData,
  setIsPreview,
  setSelectedNode,
} from '../../features/mainSlice';
import Button from '../shared/Button';

const useStyles = makeStyles(
  () => ({
    root: {
      position: 'fixed',
      top: 0,
      color: color.$3,
      backgroundColor: color.$7,
      boxShadow: 'unset',
    },
    toolbar: {
      height: 'inherit',
      borderBottom: `1px solid ${color.$9}`,
    },
    title: {
      flex: '1 1 auto',
      fontSize: 20,
      margin: '0 0 0 12px',
      display: 'flex',
      alignItems: 'center',
    },
    avatar: {
      display: 'flex',
      alignItems: 'flex-end',
    },
    left: {
      display: 'flex',
      alignItems: 'center',
    },
    image: {
      marginLeft: 32,
      borderRadius: 28,
    },
    text: {
      fontSize: 12,
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 1,
      color: color.$10,
      marginRight: 12,
    },
    onlyView: {
      padding: '5px 8px',
      color: color.$12,
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      marginLeft: 8,
      backgroundColor: color.$8,
      borderRadius: 4,
    },
    editer: {
      padding: '5px 8px',
      color: color.$2,
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      marginLeft: 8,
      backgroundColor: hexToRgba(color.$2, 10),
      borderRadius: 4,
    },
    subToolbar: {
      height: 'inherit',
      borderBottom: `1px solid ${color.$9}`,
      minHeight: 'max-content',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
    },
    tab: {
      display: 'flex',
      alignItems: 'center',
      '& > a': {
        padding: '15.5px 16px',
        fontSize: '13px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        letterSpacing: 'normal',
        color: color.$11,
        cursor: 'pointer',
      },
    },
    tool: {
      display: 'flex',
      alignItems: 'center',
      '& > *': {
        display: 'flex',
        alignItems: 'center',
        fontSize: '15px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: '1',
        letterSpacing: 'normal',
        color: color.$10,
        cursor: 'pointer',
      },
      '& img': {
        display: 'flex',
        alignItems: 'center',
        marginRight: 4,
      },
      '& > div + div': {
        marginLeft: 16,
      },
    },
    hr: {
      width: 1,
      height: 22,
      background: color.$9,
      margin: '0 12px',
    },
  }),
  {
    name: 'Header',
  },
);

let image = null;

function Header() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { orgId, procedureId } = useParams();
  const [imageSrc, setImageSrc] = useState(null);
  const { data, isLoading, isActive } = useSelector(store => store.ma);
  const { icon } = useSelector(store => store.organization);
  const { updatedAt, status } = data;

  const onPreview = () => {
    dispatch(setSelectedNode(null));
    dispatch(setIsPreview(true));
  };

  const onSave = () => {
    dispatch(saveData(orgId, procedureId));
  };

  const onCancelStepLink = () => {};

  useEffect(() => {
    if (icon && imageSrc !== icon) {
      image = new Image();
      image.onload = () => {
        setImageSrc(image.currentSrc);
      };
      image.src = icon;
    }

    return () => {
      if (image) {
        image.onload = () => {};
      }
    };
  }, [icon, imageSrc]);

  return (
    <AppBar className={classes.root} position="static">
      <Toolbar className={classes.toolbar}>
        <img src={logo} alt="logo" width="32" height="32" />
        <div className={classes.title}>
          {t('header.title')}
          <div
            className={classnames({
              [classes.onlyView]: isActive,
              [classes.editer]: !isActive,
            })}
          >
            {isActive ? t('header.view') : t('header.edit')}
          </div>
        </div>
        <div className={classes.avatar}>
          <div className={classes.text}>
            {t('header.updatedAt', {
              date: moment(updatedAt || undefined).format('YYYY-MM-DD HH:mm'),
            })}
          </div>
          <div className={classes.left}>
            {status !== MAStatusType.DONE &&
              status !== MAStatusType.TERMINATED && (
                <>
                  <Button
                    className={classes.button}
                    topic="primaryGreen"
                    onClick={onSave}
                    isLoading={isLoading}
                  >
                    {t('modal.save')}
                  </Button>
                  <div className={classes.hr} />
                </>
              )}
            <Button
              className={classes.button}
              topic="primaryGreen"
              onClick={onPreview}
            >
              {t('modal.preview')}
            </Button>
            <img
              className={classes.image}
              src={imageSrc || defaultImageUrl}
              alt="org-img"
              width={40}
              height={40}
            />
          </div>
        </div>
      </Toolbar>
      <Toolbar className={classes.subToolbar}>
        <div className={classes.tab}>
          <NavLink
            className={classnames(classes.navLink, {
              [classes['root-error']]: false,
            })}
            onClick={onCancelStepLink}
            to={`/${orgId}/${procedureId}/${TabType.SETTING}`}
            activeStyle={{
              color: color.$3,
              borderBottom: `2px solid ${color.$3}`,
              padding: '15.5px 16px 13.5px',
            }}
          >
            {t('header.setting')}
          </NavLink>
          <NavLink
            className={classnames(classes.navLink, {
              [classes['root-error']]: false,
            })}
            onClick={onCancelStepLink}
            to={`/${orgId}/${procedureId}/${TabType.RULE}`}
            activeStyle={{
              color: color.$3,
              borderBottom: `2px solid ${color.$3}`,
              padding: '15.5px 16px 13.5px',
            }}
          >
            {t('header.rule')}
          </NavLink>
        </div>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
